import { useParams } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import caseStudies from './caseStudies';
import caseStudyContents from './caseStudyContents';

const ChevronDown = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"></path>
  </svg>
);

const ChevronUp = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7"></path>
  </svg>
);

const CaseStudyDetail = () => {
  const { id } = useParams();
  const caseStudy = caseStudies.find(cs => cs.id === id);
  const content = caseStudyContents[id] || {};
  const [isConversationExpanded, setIsConversationExpanded] = useState(false);
  const userProfileRef = useRef(null);
  const detailedActivitiesRef = useRef(null);
  const [blockHeight, setBlockHeight] = useState(320);
  const expandedConversationRef = useRef(null);

  useEffect(() => {
    if (!isConversationExpanded) {
      // Set the conversation block height to the max of the two side blocks
      const upHeight = userProfileRef.current?.offsetHeight || 0;
      const daHeight = detailedActivitiesRef.current?.offsetHeight || 0;
      setBlockHeight(Math.max(upHeight, daHeight, 320));
    }
  }, [isConversationExpanded]);

  // Height for the visible conversation area (subtract space for button)
  const visibleConversationHeight = blockHeight - 56; // 40px button + 16px gap

  // Scroll to expanded conversation after expanding
  useEffect(() => {
    if (isConversationExpanded && expandedConversationRef.current) {
      expandedConversationRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isConversationExpanded]);

  // Animation classes for smooth expand/collapse
  const animatedBlockClass =
    'transition-all duration-500 ease-in-out overflow-hidden';

  return (
    <div className="w-full pt-32 pb-16 px-6 sm:px-12 lg:px-24 space-y-12">
        
      {/* Section Title */}
      <h2 className="text-3xl font-bold border-b pb-2 flex items-baseline gap-3">
        Explore
        <span className="text-base font-normal text-gray-500">
          {caseStudy ? caseStudy.name : id}
        </span>
      </h2>

      {/* System Overview */}
      <div className="bg-gray-50 p-6 rounded shadow-lg border">
        <div className="text-gray-700">{content.systemOverview}</div>
      </div>

      {/* GIDEA Replication */}
      <div className="bg-gray-50 p-6 rounded shadow-lg border">
        <div className="text-gray-700">{content.gideaReplication}</div>
      </div>

      {/* Example Section */}
      <h2 className="text-3xl font-bold border-b pb-2 flex items-baseline gap-3">
        Example
        <span className="text-base font-normal text-gray-500">
          A glimpse into one user’s profile and conversation.
        </span>
      </h2>

      {/* User Profile, Detailed Activities, and Conversation Row */}
      <div className={`grid gap-6 ${isConversationExpanded ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
        <div ref={userProfileRef} className="bg-gray-100 p-4 rounded shadow flex flex-col">
          <div className="font-bold text-2xl mb-2 border-b border-gray-300 pb-1">User Profile</div>
          <div>{content.userProfile}</div>
        </div>
        <div ref={detailedActivitiesRef} className="bg-gray-100 p-4 rounded shadow flex flex-col">
          <div className="font-bold text-2xl mb-2 border-b border-gray-300 pb-1">Detailed Activities</div>
          <div>{content.detailedActivities}</div>
        </div>
        {!isConversationExpanded && (
          <div 
            className={`bg-gray-100 p-4 rounded shadow border flex flex-col h-full ${animatedBlockClass}`}
            style={{ height: blockHeight }}
          >
            <div className="font-bold text-2xl mb-2 border-b border-gray-300 pb-1">Conversations</div>
            <div className="relative flex-grow min-h-[120px] flex flex-col">
              <div className="overflow-hidden flex-grow relative" style={{ maxHeight: visibleConversationHeight }}>
                {content.conversation}
                {/* Gradient overlay at the bottom, always at the bottom of the scrollable area */}
                <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-12 bg-gradient-to-t from-gray-100 to-transparent" />
              </div>
            </div>
            {/* Show more button: large, bold, transparent, with chevron down */}
            <button
              onClick={() => setIsConversationExpanded(true)}
              className="mt-2 mx-auto flex items-center px-3 py-1 bg-transparent text-gray-700 text-lg font-bold transition-all"
              style={{ minWidth: 90 }}
            >
              <ChevronDown />
              <span className="ml-1">Show more</span>
            </button>
          </div>
        )}
      </div>

      {/* Expanded Conversation Section */}
      {isConversationExpanded && (
        <div ref={expandedConversationRef} className={`bg-gray-100 p-6 rounded shadow-lg border flex flex-col ${animatedBlockClass}`}
        >
          <div className="font-bold text-2xl border-b border-gray-300 pb-1 mb-4">Conversations</div>
          <div className="text-gray-700 flex-grow">
            {content.conversation}
          </div>
          <button 
            onClick={() => setIsConversationExpanded(false)}
            className="mt-6 mx-auto flex items-center px-4 py-2 bg-transparent text-gray-700 text-lg font-bold transition-all"
            style={{ minWidth: 110 }}
          >
            <ChevronUp />
            <span className="ml-1">Collapse</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseStudyDetail;
